import { AxiosError } from 'axios';

import { apiService } from '@/shared/services/apiServices';
import {
  FACILITY_EDIT_ERROR_MESSAGES,
  FACILITY_VIEW_ERROR_MESSAGES,
} from '../errors/errorMessages';
import type {
  EditFacilityFormData,
  EditFacilityPatchPayload,
} from '../types/editFacilityType';
import type { ViewFacilityResponse } from '../types/viewFacilityTypes';
import facilityEndpoints from './facilityEndpoints';

// ── Field mapping ─────────────────────────────────────────────────────────────

const FIELD_MAPPINGS: Array<{
  from: keyof EditFacilityFormData;
  to: keyof EditFacilityPatchPayload;
  normalize: (v: string) => string;
}> = [
  { from: 'name', to: 'name', normalize: (v) => v.trim() },
  { from: 'nip', to: 'nip', normalize: (v) => v.replace(/\D/g, '') },
  { from: 'phoneNumber', to: 'phoneNumber', normalize: (v) => v.trim() },
  { from: 'countryCode', to: 'countryCode', normalize: (v) => v.trim() },
  { from: 'addressLine1', to: 'addressLine1', normalize: (v) => v.trim() },
  { from: 'addressLine2', to: 'addressLine2', normalize: (v) => v.trim() },
  { from: 'city', to: 'city', normalize: (v) => v.trim() },
  { from: 'state', to: 'state', normalize: (v) => v.trim() },
  { from: 'zipCode', to: 'zipCode', normalize: (v) => v.trim() },
  { from: 'country', to: 'country', normalize: (v) => v.trim() },
];

/**
 * Builds a partial payload containing only fields that differ from the committed
 * baseline. Includes optional field changes such as clearing addressLine2 to "".
 */
export const buildFacilityDiff = (
  current: EditFacilityFormData,
  committed: EditFacilityFormData
): EditFacilityPatchPayload =>
  FIELD_MAPPINGS.reduce<EditFacilityPatchPayload>(
    (diff, { from, to, normalize }) => {
      const currentVal = normalize(current[from]);
      const committedVal = normalize(committed[from]);
      return currentVal !== committedVal ? { ...diff, [to]: currentVal } : diff;
    },
    {}
  );

// ── Result types ──────────────────────────────────────────────────────────────

export interface GetFacilityForEditResult {
  success: boolean;
  fields?: EditFacilityFormData;
  /** The non-editable sequential ID, e.g. "FACILITY000001". Null for draft facilities. */
  facilitySequentialId?: string | null;
  clientId?: string;
  employeeCount?: number;
  equipmentCount?: number;
  facilityStatus?: 'active' | 'inactive' | 'cascaded';
  error?: string;
  isAccessDenied?: boolean;
}

export interface UpdateFacilityResult {
  success: boolean;
  error?: string;
  isAccessDenied?: boolean;
}

// ── API functions ─────────────────────────────────────────────────────────────

/**
 * Fetches facility data for the edit form.
 *
 * GET /loopay-facility-management/facilities/{facilityId}
 * On success: returns form-ready fields and the non-editable facilitySequentialId.
 * On error: returns a user-facing error string.
 */
export const getFacilityForEdit = async (
  facilityId: string
): Promise<GetFacilityForEditResult> => {
  try {
    const result = await apiService.getAPI(
      facilityEndpoints.FACILITY_DRAFT_GET(facilityId)
    );

    if (result.error) {
      if (result.status === 403) {
        return { success: false, isAccessDenied: true };
      }
      return {
        success: false,
        error: result.isNetworkError
          ? FACILITY_EDIT_ERROR_MESSAGES.NETWORK_ERROR
          : FACILITY_EDIT_ERROR_MESSAGES.LOAD_ERROR,
      };
    }

    const data = result.data as ViewFacilityResponse;
    const f = data.facility;
    return {
      success: true,
      facilitySequentialId: f.facilityId ?? null,
      clientId: f.clientId,
      employeeCount: f.facilityUserCount,
      equipmentCount: f.equipmentCount,
      facilityStatus: f.status,
      fields: {
        name: f.name ?? '',
        nip: f.nip ?? '',
        phoneNumber: f.phoneNumber ?? '',
        countryCode: f.countryCode ?? '',
        addressLine1: f.addressLine1 ?? '',
        addressLine2: f.addressLine2 ?? '',
        city: f.city ?? '',
        state: f.state ?? '',
        zipCode: f.zipCode ?? '',
        country: f.country ?? '',
      },
    };
  } catch (err) {
    if (err instanceof AxiosError && !err.response) {
      return {
        success: false,
        error: FACILITY_EDIT_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }
    return {
      success: false,
      error: FACILITY_EDIT_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};

/**
 * PATCHes a facility's profile fields with only the changed values.
 *
 * PATCH /loopay-facility-management/facilities/{facilityId}
 * Sends { ...diff, fillMandatory: false }.
 * Any 403 response is replaced with a generic ACCESS_DENIED message to avoid
 * surfacing verbose AVP error strings to the user.
 */
export const updateFacility = async (
  facilityId: string,
  diff: EditFacilityPatchPayload
): Promise<UpdateFacilityResult> => {
  try {
    const result = await apiService.patchAPI(
      facilityEndpoints.FACILITY_PATCH(facilityId),
      { ...diff, fillMandatory: false },
      { skipGlobalErrorRedirect: true }
    );

    if (result.error) {
      if (result.status === 403) {
        return {
          success: false,
          isAccessDenied: true,
          error: FACILITY_VIEW_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
        };
      }
      return {
        success: false,
        error: result.isNetworkError
          ? FACILITY_EDIT_ERROR_MESSAGES.NETWORK_ERROR
          : FACILITY_EDIT_ERROR_MESSAGES.DEFAULT_ERROR,
      };
    }

    return { success: true };
  } catch (err) {
    if (err instanceof AxiosError) {
      if (err.response?.status === 403) {
        return {
          success: false,
          isAccessDenied: true,
          error: FACILITY_VIEW_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
        };
      }
      const isNetworkError = !err.response && !!err.request;
      return {
        success: false,
        error: isNetworkError
          ? FACILITY_EDIT_ERROR_MESSAGES.NETWORK_ERROR
          : FACILITY_EDIT_ERROR_MESSAGES.UNEXPECTED_ERROR,
      };
    }
    return {
      success: false,
      error: FACILITY_EDIT_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
